import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import InputMask from "react-input-mask";
import toast from "react-hot-toast";

import { createAccount } from "../../redux/actions/session";
import MobileMenu from "../../components/menuMobile/MenuMobile";

import logo from "../../assets/images/logo-trucoxo.png";
import "./NewAccount.scss";

const PASSWORD_RULES = [
  { id: "len",     label: "8 caracteres",                      test: (s) => s.length >= 8 },
  { id: "upper",   label: "1 letra maiúscula",                 test: (s) => /[A-Z]/.test(s) },
  { id: "lower",   label: "1 letra minúscula",                 test: (s) => /[a-z]/.test(s) },
  { id: "special", label: "1 caractere especial (!@#$%¨&*)",   test: (s) => /[!@#$%¨&*]/.test(s) },
  { id: "number",  label: "1 número",                          test: (s) => /\d/.test(s) },
];

const NewAccount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [nickname, setNickname] = useState("");
  const [cellphone, setCellphone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [termsOfUse, setTermsOfUse] = useState(false);
  const [promo, setPromo] = useState(false);

  const [agent, setAgent] = useState(false);
  const [agentCode, setAgentCode] = useState("");
  const [urlAgentCode, setUrlAgentCode] = useState("");

  const [emailError, setEmailError] = useState("");
  const [nicknameError, setNicknameError] = useState("");
  const [cellphoneError, setCellphoneError] = useState("");

  useEffect(() => {
    const queryParameters = new URLSearchParams(window.location.search);
    setUrlAgentCode(queryParameters.get("agent_code"));
  }, []);

  const passwordChecks = useMemo(
    () => PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(password) })),
    [password]
  );

  const passwordValid = passwordChecks.every((c) => c.ok);

  const validateNickname = () => {
    if (nickname.trim().length === 0) setNicknameError("Campo obrigatório");
    else if (nickname.trim().length > 10) setNicknameError("Máximo 10 caracteres");
    else setNicknameError("");
  };

  const validateCellphone = () => {
    const phoneRaw = cellphone.replace(/[^0-9]/g, "");
    if (phoneRaw.length === 0) setCellphoneError("Campo obrigatório");
    else if (phoneRaw.length !== 11) setCellphoneError("Número inválido");
    else setCellphoneError("");
  };

  const validateEmail = () => {
    if (email.trim().length === 0) setEmailError("Campo obrigatório");
    else if (!email.trim().match(/[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/))
      setEmailError("Email inválido");
    else setEmailError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!termsOfUse) {
      return toast.error("Aceite os termos de uso antes de continuar");
    }
    if (!passwordValid) {
      return toast.error("Senha não atende aos critérios");
    }
    if (emailError || nicknameError || cellphoneError) {
      return toast.error("Preencha todos os campos corretamente");
    }
    const phoneRaw = cellphone.replace(/[^0-9]/g, "");
    dispatch(
      createAccount({
        email,
        nickname,
        cellphone: phoneRaw,
        password,
        agentCode: agentCode || urlAgentCode || "",
      })
    );
  };

  return (
    <>
      <div className="signup-page">
        <button
          type="button"
          className="signup-page__close"
          onClick={() => navigate("/")}
          aria-label="Fechar"
        >
          <i className="fa-solid fa-xmark" /> Fechar
        </button>

        <form className="signup-card" onSubmit={handleSubmit} noValidate>
          <div className="signup-card__brand">
            <img src={logo} alt="Truco XP" />
          </div>

          <header className="signup-card__header">
            <h1>Criar minha conta</h1>
            <p>Insira todas as informações</p>
          </header>

          {/* Nome de usuário */}
          <label className="signup-field">
            <span className="signup-field__label">Nome de usuário</span>
            <input
              type="text"
              className="signup-field__input"
              placeholder="Crie um nome de usuário"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              onBlur={validateNickname}
              onFocus={() => {
                setNicknameError("");
                setNickname(nickname.trim());
              }}
            />
            {nicknameError && (
              <span className="signup-field__error">{nicknameError}</span>
            )}
          </label>

          {/* Celular */}
          <label className="signup-field">
            <span className="signup-field__label">Celular</span>
            <InputMask
              mask="(99) 99999-9999"
              className="signup-field__input"
              placeholder="(00) 00000-0000"
              value={cellphone}
              onChange={(e) => setCellphone(e.target.value)}
              onBlur={validateCellphone}
              onFocus={() => setCellphoneError("")}
              inputMode="numeric"
            />
            {cellphoneError && (
              <span className="signup-field__error">{cellphoneError}</span>
            )}
          </label>

          {/* E-mail */}
          <label className="signup-field">
            <span className="signup-field__label">E-mail</span>
            <input
              type="email"
              className="signup-field__input"
              placeholder="Digite seu e-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={validateEmail}
              onFocus={() => {
                setEmailError("");
                setEmail(email.trim());
              }}
            />
            {emailError && (
              <span className="signup-field__error">{emailError}</span>
            )}
          </label>

          {/* Senha */}
          <label className="signup-field">
            <span className="signup-field__label">Senha</span>
            <div className="signup-field__input-wrap">
              <input
                type={showPassword ? "text" : "password"}
                className="signup-field__input"
                placeholder="Crie uma senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="signup-field__toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
              </button>
            </div>
          </label>

          {/* Critérios da senha — só aparece ao digitar */}
          {password.length > 0 && (
            <ul className="password-rules">
              {passwordChecks.map((rule) => (
                <li
                  key={rule.id}
                  className={`password-rules__item ${
                    rule.ok ? "password-rules__item--ok" : ""
                  }`}
                >
                  <span className="password-rules__icon" aria-hidden="true">
                    {rule.ok ? (
                      <i className="fa-solid fa-check" />
                    ) : (
                      <span className="password-rules__bullet" />
                    )}
                  </span>
                  <span className="password-rules__label">{rule.label}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Toggle agente */}
          {!urlAgentCode && (
            <div className="signup-toggle">
              <label className="signup-toggle__switch">
                <input
                  type="checkbox"
                  checked={agent}
                  onChange={(e) => {
                    setAgent(e.target.checked);
                    if (!e.target.checked) setAgentCode("");
                  }}
                />
                <span className="signup-toggle__slider" />
              </label>
              <span className="signup-toggle__label">Possui agente?</span>
            </div>
          )}

          {agent && (
            <label className="signup-field">
              <span className="signup-field__label">Código do agente</span>
              <input
                type="text"
                className="signup-field__input"
                value={agentCode}
                onChange={(e) => setAgentCode(e.target.value)}
              />
            </label>
          )}

          {/* Checkboxes */}
          <div className="signup-checks">
            <label className="signup-check">
              <input
                type="checkbox"
                checked={promo}
                onChange={() => setPromo(!promo)}
              />
              <span className="signup-check__box" />
              <span className="signup-check__label">
                Aceito receber notificações promocionais
              </span>
            </label>

            <label className="signup-check">
              <input
                type="checkbox"
                checked={termsOfUse}
                onChange={() => setTermsOfUse(!termsOfUse)}
              />
              <span className="signup-check__box" />
              <span className="signup-check__label">
                Eu aceito os Termos de Uso e tenho mais de 18 anos.
              </span>
            </label>
          </div>

          <button type="submit" className="signup-submit">
            Criar conta <i className="fa-solid fa-arrow-right" />
          </button>

          <Link to="/login" className="signup-card__login-link">
            Já tem uma conta? Acesse
          </Link>
        </form>

        <footer className="signup-page__footer">
          <span>trucoxp©</span>
          <span>{new Date().getFullYear()}</span>
        </footer>
      </div>

      <MobileMenu />
    </>
  );
};

export default NewAccount;
